/**
 * ChatSlice.tsx
 *
 * Redux slice for chat state management.
 * Handles My Chat, User Chat, and All Chat features.
 *
 * Refactored: Removed commented-out dead code (~700 lines).
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import InitialState, { UpdateChatListAction } from "./ChatRedux"
import { Collaboration, Room } from "src/app/models/Chat"
import Message from "src/app/models/Message"
import { ChatType } from "src/app/modules/chat/feature"
import { Order } from "src/app/models/Order"

// ============================================================================
// Initial State
// ============================================================================

const initialState: InitialState = {
  // My Chat (Handled Customer)
  chatList: [],
  selectedCollaboration: undefined,
  selectedRoom: undefined,
  lastVisibleMessage: undefined,
  listMessage: [],
  countTotalUnreadMessages: 0,
  countSelectedUnreadMessage: 0,
  replyToMessage: "",
  replyToMessageAudioDuration: "",

  // User Chat (Friend-to-Friend)
  userListMessage: [],
  userChatList: [],
  countTotalUserUnreadMessages: 0,
  countSelectedUserUnreadMessage: 0,
  selectedUserChat: "",

  // All Chat
  allChatListMessage: [],
  allChatSelectedChat: "",
  allChatSelectedRoom: "",
  allChatCountTotalUnreadMessages: 0,
  allChatCountSelectedUnreadMessage: 0,
  allChatLastVisibleMessage: undefined,

  // General
  chatState: ChatType.mychat,
  chatInput: [],
  orderInitiateMessage: undefined,
}

// ============================================================================
// Slice Definition
// ============================================================================

export const ChatSlice = createSlice({
  name: UpdateChatListAction,
  initialState,
  reducers: {
    // ======================== My Chat Reducers ========================

    setChatList: (state, action: PayloadAction<Collaboration[]>) => {
      state.chatList = action.payload
    },

    updateChatList: (state, action: PayloadAction<Collaboration>) => {
      // Updates handled externally via chatAct
    },

    updateCollabIncommingMessage: (state, action: PayloadAction<Collaboration>) => {
      // Updates handled externally
    },

    setOrderInitiateMessage: (state, action: PayloadAction<Order | undefined>) => {
      state.orderInitiateMessage = action.payload
    },

    updateUnreadMessage: (state, action: PayloadAction<string>) => {
      // Unread message updates handled externally
    },

    setListMessages: (state, action: PayloadAction<Message[]>) => {
      state.listMessage = action.payload
    },

    setChatInput: (state, action: PayloadAction<any>) => {
      state.chatInput = action.payload
    },

    setSelectedRoom: (state, action: PayloadAction<Room | undefined>) => {
      state.selectedRoom = action.payload
    },

    setSelectedCollaboration: (state, action: PayloadAction<Collaboration | undefined>) => {
      state.selectedCollaboration = action.payload
    },

    addIncomingMessages: (state, action: PayloadAction<Message>) => {
      if (state.selectedCollaboration?.id === action.payload.collaboration?.id) {
        state.listMessage = [...state.listMessage, action.payload]
      }
    },

    deleteMessage: (state, action: PayloadAction<boolean>) => {
      // Deletion handled externally
    },

    setLastVisibleMessage: (state, action: PayloadAction<any>) => {
      state.lastVisibleMessage = action.payload
    },

    updateStatusListMessages: (state, action: PayloadAction<Message>) => {
      state.listMessage = state.listMessage.map((item) => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            status: action.payload.status,
            updatedAt: action.payload.updatedAt,
            submittedAt: action.payload.submittedAt,
            deliveredAt: action.payload.deliveredAt,
            readAt: action.payload.readAt,
          }
        }
        return item
      })
    },

    updateActiveListMessages: (state, action: PayloadAction<Message>) => {
      state.listMessage = state.listMessage.map((item) => {
        if (item.id === action.payload.id) {
          return { ...item, status: action.payload.status }
        }
        return item
      })
    },

    // ======================== User Chat Reducers ========================

    setUserChatList: (state, action: PayloadAction<Collaboration[]>) => {
      state.userChatList = action.payload
    },

    setSelectedUserChat: (state, action: PayloadAction<string>) => {
      state.selectedUserChat = action.payload
    },

    updateSortUserChatList: (state) => {
      // Sorting handled externally via chatAct
    },

    updateUserChatList: (state, action: PayloadAction<Collaboration>) => {
      // Updates handled externally
    },

    addUserIncomingMessages: (state, action: PayloadAction<Message>) => {
      if (state.selectedUserChat === action.payload.collaboration?.id) {
        state.userListMessage = [...state.userListMessage, action.payload]
      }
    },

    updateUserUnreadMessage: (state, action: PayloadAction<string>) => {
      // Unread updates handled externally
    },

    setUserListMessages: (state, action: PayloadAction<Message[]>) => {
      state.userListMessage = action.payload
    },

    // ======================== All Chat Reducers ========================

    setAllChatChatList: (state, action: PayloadAction<Collaboration[]>) => {
      // List setting handled externally
    },

    updateAllChatChatList: (state, action: PayloadAction<Collaboration>) => {
      // Updates handled externally
    },

    updateAllChatCollabIncommingMessage: (state, action: PayloadAction<Collaboration>) => {
      // Updates handled externally
    },

    updateAllChatUnreadMessage: (state, action: PayloadAction<string>) => {
      // Unread updates handled externally
    },

    setAllChatListMessages: (state, action: PayloadAction<Message[]>) => {
      state.allChatListMessage = action.payload
    },

    setAllChatSelectedChat: (state, action: PayloadAction<string>) => {
      state.allChatSelectedChat = action.payload
    },

    setAllChatSelectedRoom: (state, action: PayloadAction<string>) => {
      state.allChatSelectedRoom = action.payload
    },

    setAllChatSelectedCollaboration: (state, action: PayloadAction<Collaboration | undefined>) => {
      state.allChatSelectedCollaboration = action.payload
    },

    addAllChatIncomingMessages: (state, action: PayloadAction<Message>) => {
      if (state.allChatSelectedChat === action.payload.collaboration?.id) {
        state.allChatListMessage = [...state.allChatListMessage, action.payload]
      }
    },

    deleteAllChatMessage: (state, action: PayloadAction<boolean>) => {
      // Deletion handled externally
    },

    updateAllChatStatusListMessages: (state, action: PayloadAction<Message>) => {
      state.allChatListMessage = state.allChatListMessage.map((item) => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            status: action.payload.status,
            updatedAt: action.payload.updatedAt,
            submittedAt: action.payload.submittedAt,
            deliveredAt: action.payload.deliveredAt,
            readAt: action.payload.readAt,
          }
        }
        return item
      })
    },

    updateAllChatActiveListMessages: (state, action: PayloadAction<Message>) => {
      state.allChatListMessage = state.allChatListMessage.map((item) => {
        if (item.id === action.payload.id) {
          return { ...item, status: action.payload.status }
        }
        return item
      })
    },

    setAllChatLastVisibleMessage: (state, action: PayloadAction<any>) => {
      state.allChatLastVisibleMessage = action.payload
    },

    // ======================== General Reducers ========================

    setReplyToMessage: (state, action: PayloadAction<any>) => {
      state.replyToMessage = action.payload
    },

    setReplyToMessageAudioDuration: (state, action: PayloadAction<any>) => {
      state.replyToMessageAudioDuration = action.payload
    },

    setCurrentChatState: (state, action: PayloadAction<string>) => {
      state.chatState = action.payload
    },
  },
})

// ============================================================================
// Exports
// ============================================================================

export const {
  // My Chat
  setChatList,
  setListMessages,
  setChatInput,
  setSelectedCollaboration,
  setSelectedRoom,
  updateUnreadMessage,
  addIncomingMessages,
  deleteMessage,
  setLastVisibleMessage,
  updateChatList,
  updateStatusListMessages,
  updateActiveListMessages,
  updateCollabIncommingMessage,
  setOrderInitiateMessage,

  // User Chat
  setUserListMessages,
  setUserChatList,
  updateUserChatList,
  updateSortUserChatList,
  updateUserUnreadMessage,
  setSelectedUserChat,
  addUserIncomingMessages,

  // All Chat
  setAllChatChatList,
  updateAllChatChatList,
  updateAllChatUnreadMessage,
  setAllChatListMessages,
  setAllChatSelectedChat,
  setAllChatSelectedRoom,
  setAllChatSelectedCollaboration,
  addAllChatIncomingMessages,
  deleteAllChatMessage,
  updateAllChatStatusListMessages,
  updateAllChatActiveListMessages,
  setAllChatLastVisibleMessage,
  updateAllChatCollabIncommingMessage,

  // General
  setReplyToMessage,
  setReplyToMessageAudioDuration,
  setCurrentChatState,
} = ChatSlice.actions

export default ChatSlice.reducer
